import React, { useEffect, useState } from "react";
import "./scss/custom.scss";
import config from "clientappsetting.json";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { initialStep, StepId } from "logic/Navigation/Steps";
import { NewBookingRoot } from "views/NewBooking/NewBookingRoot";
import { NewBooking } from "types/NewBooking";
import { ContactDetails } from "components/ContactDetails";
import { LoadingBar } from "components/LoadingBar";
import { MasterLayout } from "components/layout/MasterLayout";
import { convertExistingBookingToInputData } from "helpers/BookingConversionHelper";
import { ErrorPage } from "views/ErrorPage";
import { ExistingBooking } from "views/ExistingBooking";
import { DriverMicrositeStatus, MicrositePageDetails, DriverMicrositeTheme, DriverMicrositeFont } from "generated/types/types";
import Favicon from "react-favicon";
import { ApiUrlResolver } from "./utils/ApiUrlResolver";
import { useDispatch } from "react-redux";
import { setCountryCode } from "./redux/store/slices/countrySlice";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/da";
import "dayjs/locale/en-gb";
import { setBaseURL } from "api/HttpClient";

const baseUrl = document.getElementsByTagName("base")[0].getAttribute("href");

function App() {
    const [initialInputData, setInitialInputData] = useState<NewBooking>({
        previousBookingId: null,
        vehicleDetails: null,
        workDetails: null,
        driverDetails: null,
        selectedServiceCentreDetails: null,
    });
    const [currentStep, setCurrentStep] = useState<StepId>(initialStep);
    const [pageDetails, setPageDetails] = useState<MicrositePageDetails | null>(null);
    const [cannotLoadPage, setCannotLoadPage] = useState<boolean>(false);
    const [micrositePin, setMicrositePin] = useState<string | null>(null);
    const [isBookingCreationJourney, setIsBookingCreationJourney] = useState<boolean | null>(null);
    const [headerImage, setHeaderImage] = useState<string | null>(null);
    const [faviconImage, setFavIconImage] = useState<string | null>(null);
    const [fontLoading, setFontLoading] = useState<boolean>(true);
    const [themeColoursLoading, setThemeColoursLoading] = useState<boolean>(true);
    const [imagesLoading, setImagesLoading] = useState<boolean>(true);
    const [footerImage, setFooterImage] = useState<string | null>(null);
    const [footerStraplineText, setFooterStraplineText] = useState<string | null>(null);
    const [previousStep, setPreviousStep] = useState<StepId>(initialStep);
    const [apiUrl, setApiUrl] = useState<string | null>(null);
    const countryCode = useSelector((state: RootState) => state.country.countryCode);
    const { t } = useTranslation();

    const pathname = window.location.pathname;
    const dispatch = useDispatch();

    const updateSteps = (newStep: StepId) => {
        setPreviousStep(currentStep);
        setCurrentStep(newStep);
    }

    useEffect(() => {
        if (process.env.NODE_ENV !== "production") {
            (window as any).setLang = (lng: "en" | "dk") => i18n.changeLanguage(lng);
        }
    }, [i18n]);

    useEffect(() => {
        if (apiUrl && micrositePin) {
            fetch(ApiUrlResolver.getSettingAPIUrl(countryCode) + `/DriverMicrosite/PageDetails/${micrositePin}`)
                .then(response => response.json())
                .then((data: MicrositePageDetails) => {
                    setPageDetails(data);
                    setTitleName(data.micrositeSetting.title);
                })
                .catch(() => setCannotLoadPage(true));
        }
    }, [apiUrl, micrositePin]);

    function mapCountryToLang(cc?: string | null): "en" | "dk" {
        switch ((cc || "").trim().toUpperCase()) {
            case "DK":
            return "dk";         // i18next key
            case "GB":
            case "UK":
            case "":
            default:
            return "en";
        }
    }

    function pickLang(cc?: string | null): "en" | "dk" {
        const qp = new URLSearchParams(window.location.search).get("lng")?.toLowerCase();
        if (qp === "en" || qp === "dk") return qp;           // QA override via ?lng=en|dk
        return mapCountryToLang(cc);                          // normal country mapping
    }

    function applyLang(raw: string): void {
        const v = (raw || "").toLowerCase();
        const lang: "en" | "dk" = v === "dk" || v === "da" ? "dk" : "en";

        i18n.changeLanguage(lang).catch(() => {});

        document.documentElement.lang = lang === "dk" ? "da-DK" : "en-GB";
        dayjs.locale(lang === "dk" ? "da" : "en-gb");
    }

    function fetchMicrositeCountryCode(micrositePin: string) {
        console.log("config.DbmInternational_Switch", config.DbmInternational_Switch);
        console.log("config.DbmInternational_Switch_Enabled", config.DbmInternational_Switch_Enabled);
        console.log("config.DbmInternational_Switch_Cond", (config.DbmInternational_Switch == config.DbmInternational_Switch_Enabled));

        if (config.DbmInternational_Switch == config.DbmInternational_Switch_Enabled) {           
            fetch(config.SETTINGS_API_URL + `/DriverMicrosite/MicrositeCountryCode/${micrositePin}`)
                .then(response => response.json())
                .then((countryCode: string) => {
                    const apiUrlToUse = countryCode === "DK" ? config.SETTINGS_API_URL : config.API_URL;
                    const baseUrl = countryCode === "DK" ? config.API_DK_URL : config.API_URL;
                    setApiUrl(apiUrlToUse);
                    setBaseURL(baseUrl);
                    dispatch(setCountryCode(countryCode));
                    applyLang(pickLang(countryCode));
                })
                .catch(() => {
                    dispatch(setCountryCode(""));
                    setApiUrl(config.API_URL);
                    setBaseURL(config.API_URL);
                    applyLang(pickLang(null));
                });
        }
        else {
            dispatch(setCountryCode(""));
            setApiUrl(config.API_URL);
            applyLang(pickLang(null));
        }
    }

    async function fetchMicrositeCountryCodeForBooking(encryptedBookingId: string) {
        console.log("config.DbmInternational_Switch", config.DbmInternational_Switch);
        console.log("config.DbmInternational_Switch_Enabled", config.DbmInternational_Switch_Enabled);
        console.log("config.DbmInternational_Switch_Cond", (config.DbmInternational_Switch == config.DbmInternational_Switch_Enabled));

        if (config.DbmInternational_Switch == config.DbmInternational_Switch_Enabled) {
            try{
                  const response = await fetch(config.SETTINGS_API_URL + `/DriverMicrosite/MicrositeCountryCodeByBookingId/${encryptedBookingId}`);
                  const countryCode = await response.json();
                    const apiUrlToUse = countryCode === "DK" ? config.SETTINGS_API_URL : config.API_URL;
                    const baseUrl = countryCode === "DK" ? config.API_DK_URL : config.API_URL;
                    setApiUrl(apiUrlToUse);
                    setBaseURL(baseUrl);
                    dispatch(setCountryCode(countryCode));
                    applyLang(pickLang(countryCode));
                    return countryCode;
            }      
            catch(error)
            {
                dispatch(setCountryCode(""));
                setApiUrl(config.API_URL);
                setBaseURL(config.API_URL);
                applyLang(pickLang(null));
                return "";
            }     
        }
        else {
            dispatch(setCountryCode(""));
            setApiUrl(config.API_URL);
            applyLang(pickLang(null));
            return "";
        }
    }


    useEffect(() => {
        const fetchThemeFonts = (fontIds: string) => {
            return fetch(ApiUrlResolver.getSettingAPIUrl(countryCode) + `/DriverMicrosite/Fonts/${encodeURIComponent(fontIds)}`)
                .then(response => response.json())
                .then((data: DriverMicrositeFont[]) => {
                    loadFontFamily(data);
                });
        };

        if (micrositePin && apiUrl) {
            fetch(
                ApiUrlResolver.getSettingAPIUrl(countryCode) + `/DriverMicrosite/MicrositeTheme/${micrositePin}`)
                .then(response => response.json())
                .then((data: DriverMicrositeTheme) => {
                    setThemeColours(data);
                    setThemeImagesData(data);
                    setFooterStraplineText(data.footerStraplineText);
                    if (data.fonts) {
                        fetchThemeFonts(data.fonts);
                    } else {
                        setFontLoading(false);
                    }
                })
                //If no theme found set the logo to a default of empty string so the loading bar stops showing
                .catch(() => {
                    setImagesLoading(false);
                    setFontLoading(false);
                    setThemeColoursLoading(false);
                });
        }
    }, [apiUrl, micrositePin]);

    useEffect(() => {
        const pathComponents = pathname.split("/");
        const isOnBookingLandingPage = pathComponents.length >= 2 && pathComponents[pathComponents.length - 2].toLowerCase() === "manage";
        setIsBookingCreationJourney(!isOnBookingLandingPage);
        if (isOnBookingLandingPage) {
            const loadBookingData = async () => {
            try{
            const encryptedBookingId = pathComponents[pathComponents.length - 1];
            const countryCodeResponse = await fetchMicrositeCountryCodeForBooking(encryptedBookingId);
            fetch(ApiUrlResolver.getSettingAPIUrl(countryCodeResponse) + `/DriverMicrosite/micrositePinForBooking/${encryptedBookingId}`)
                .then(response => response.text())
                .then(micrositePin => {
                    if (micrositePin.length > 0) {
                        setMicrositePin(micrositePin);
                    } else {
                        setCannotLoadPage(true);
                    }
                });}
                catch (error) {
                    console.error("Error loading booking data:", error);
                }};
                loadBookingData();
        } else {
            const pin = pathComponents[pathComponents.length - 1];
            setMicrositePin(pin);
            fetchMicrositeCountryCode(pin);
        }
    }, [pathname]);

    if (cannotLoadPage) {
        return <ErrorPage title={t("app_page_not_found")} description={t("app_page_not_found_desc")} />;
    } else if (pageDetails === null || micrositePin === null || isBookingCreationJourney === null || fontLoading || themeColoursLoading || imagesLoading) {
        return <LoadingBar fullscreen={true} />;
    } else if (pageDetails.driverMicrositeStatus === DriverMicrositeStatus.Suspended || pageDetails.driverMicrositeStatus === DriverMicrositeStatus.DisabledForOperator) {
        return (
            <ErrorPage
                title={t("app_page_not_found")}
                description={t("app_site_not_available")}
            >
                <ContactDetails contactDetails={pageDetails.micrositeContactDetails} />
            </ErrorPage>
        );
    } else if (pageDetails.driverMicrositeStatus === DriverMicrositeStatus.Cancelled) {
        return (
            <ErrorPage title={t("app_page_not_found")} description={t("app_page_not_available")}>
                <ContactDetails contactDetails={pageDetails.micrositeContactDetails} />
            </ErrorPage>
        );
    } else {
        return (
            <MasterLayout
                contactDetails={pageDetails.micrositeContactDetails}
                currentStep={currentStep}
                setCurrentStep={updateSteps}
                isBookingCreationJourney={isBookingCreationJourney}
                headerImageBlobGuid={headerImage}
                favIconBlobGuid={faviconImage}
                micrositePin={micrositePin}
                footerImageBlobGuid={footerImage}
                footerStraplineText={footerStraplineText}
                customCompanyName={pageDetails.micrositeSetting.customCompanyName}
            >
                <BrowserRouter basename={baseUrl || undefined}>
                    <Switch>
                        <Route
                            exact
                            path="/manage/:encryptedBookingId"
                            render={props => (
                                <ExistingBooking
                                    {...props}
                                    populateNewBookingState={existingBooking => {
                                        setInitialInputData(convertExistingBookingToInputData(existingBooking, t));
                                    }}
                                    micrositePin={micrositePin}
                                />
                            )}
                        />
                        <Route
                            exact
                            path="/:micrositePin"
                            render={props => (
                                <NewBookingRoot
                                    {...props}
                                    currentStep={currentStep}
                                    setCurrentStep={updateSteps}
                                    initialInputData={initialInputData}
                                    setInitialInputData={setInitialInputData}
                                    micrositeSetting={pageDetails.micrositeSetting}
                                    previousStep={previousStep}
                                />
                            )}
                        />
                    </Switch>

                    {faviconImage ? <Favicon url={`${config.API_URL}/Blob/GetBlobFile/${faviconImage}`} /> : null}

                </BrowserRouter>
            </MasterLayout>
        );
    }

    function setThemeImagesData(data: DriverMicrositeTheme) {
        try {
            setHeaderImage(data.logoImage.split(";")[0]);
            setFavIconImage(data.favIcon.split(";")[0]);
            setFooterImage(data.footerImage.split(";")[0]);
            setImagesLoading(false);
        } catch {
            setImagesLoading(false);
        }
    }

    function setThemeColours(data: DriverMicrositeTheme) {
        try {
            document.documentElement.style.setProperty("--primary-color", data.primaryColor.toString());
            document.documentElement.style.setProperty("--dark-primary-color", data.darkPrimaryColor.toString());
            document.documentElement.style.setProperty("--light-primary-color", data.lightPrimaryColor.toString());
            setThemeColoursLoading(false);
        } catch {
            setThemeColoursLoading(false);
        }
    }

    function loadFontFamily(fonts: DriverMicrositeFont[]) {
        try {
            let fontNames = "";
            if (fonts.length > 0) {
                const fontLoaders: Promise<FontFace | void>[] = [];
                fonts.forEach((element, index) => {
                    if (element.fontBlobGuid) {
                        const junction_font = new FontFace(`customfont${index}`, `url(${config.API_URL}/Blob/GetBlobFile/${element.fontBlobGuid}`);
                        fontLoaders.push(
                            junction_font.load().then(function (loaded_face) {
                                document.fonts.add(loaded_face);
                            })
                        );
                        fontNames += fontNames ? `, customfont${index}` : `customfont${index}`;
                    } else {
                        fontNames += fontNames ? `, ${element.fontName}` : element.fontName;
                    }
                });

                // Set font family css variable
                document.documentElement.style.setProperty("--theme-fonts", fontNames);

                // Once all of the custom fonts have loaded set font loading to false
                Promise.all(fontLoaders).then(() => setFontLoading(false));
            } else {
                setFontLoading(false);
            }
        } catch {
            setFontLoading(false);
        }
    }

    function setTitleName(titleName: string) {
        document.title = titleName;
    }

}

export default App;
